import styled, { css, DefaultTheme } from 'styled-components'

import { TextAreaProps } from '.'

type PropsTextArea = Pick<
  TextAreaProps,
  | 'bgColor'
  | 'outline'
  | 'outlineColor'
  | 'labelColor'
  | 'textColor'
  | 'fontSize'
  | 'labelSize'
  | 'labelWeight'
  | 'labelOverlapping'
  | 'disabled'
  | 'radius'
  | 'labelBgColor'
  | 'outlineFocusColor'
>

type WrapperProps = Pick<TextAreaProps, 'disabled'> & { error?: boolean }

type AsteriskProps = Pick<TextAreaProps, 'requiredColor'>

export const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`

export const Input = styled.textarea<PropsTextArea>`
  ${({
    theme,
    radius,
    bgColor,
    outline,
    outlineColor,
    textColor,
    fontSize,
    outlineFocusColor
  }) => css`
    color: ${theme.ds.colors[textColor!]};
    font-family: ${theme.ds.typography.fontFamily};
    font-size: ${theme.ds.typography.size[fontSize!]};
    line-height: ${theme.ds.typography.lineHeight.medium};
    background-color: ${theme.ds.colors[bgColor!]};
    padding: 8px 16px;
    border: 1px solid
      ${outline ? theme.ds.colors[outlineColor!] : theme.ds.colors.neutral300};
    border-radius: ${theme.ds.typography.border.radius[radius!]};
    outline: none;
    resize: none;
    width: 100%;

    &::placeholder {
      color: ${theme.ds.colors.neutral500};
      font-weight: ${theme.ds.typography.weight.normal};
      opacity: 1;
    }

    &:focus {
      border-color: outlineFocusColor ? ${
        theme.ds.colors[outlineFocusColor!]
      } : ${theme.ds.colors.primary500};
    }
  `}
`

export const LabelWrapper = styled.div`
  position: relative;
  display: flex;
`

export const Label = styled.label<PropsTextArea>`
  ${({
    theme,
    disabled,
    labelColor,
    labelSize,
    labelWeight,
    labelOverlapping,
    labelBgColor
  }) => css`
    display: block;
    font-size: ${theme.ds.typography.size[labelSize!]};
    font-weight: ${theme.ds.typography.weight[labelWeight!]};
    color: ${theme.ds.colors[labelColor!]};
    cursor: pointer;
    margin-bottom: 8px;

    ${labelOverlapping &&
    css`
      position: absolute;
      z-index: 10;
      top: 0;
      left: 14px;
      transform: translateY(-50%);
      margin-bottom: 0px;
      padding: 0 2px;
      line-height: ${theme.ds.typography.lineHeight.small};
      background-color: ${disabled
        ? theme.ds.colors.neutral25
        : labelBgColor
        ? theme.ds.colors[labelBgColor!]
        : theme.ds.colors.white};
    `}
  `}
`

export const Icon = styled.div<PropsTextArea>`
  display: flex;
  color: ${({ theme }) => theme.colors.gray};
  margin-top: 8px;
  cursor: pointer;
  & > svg {
    height: 54px;
  }
`

export const Error = styled.p`
  color: ${({ theme }) => theme.colors.lightRed};
  font-size: ${({ theme }) => theme.font.sizes.xsmall};
`

const wrapperModifiers = {
  error: (theme: DefaultTheme) => css`
    ${Input} {
      border-color: ${theme.colors.lightRed};
      border-width: 1px;
      border-style: solid;
    }
    ${Icon} {
      color: ${theme.colors.lightRed};
    }
  `,
  disabled: (theme: DefaultTheme) => css`
    ${Label},
    ${Input},
    ${Icon} {
      &::placeholder {
        color: currentColor;
      }
    }
    ${Input} {
      background-color: ${theme.ds.colors.neutral50};
      border-color: ${theme.ds.colors.neutral200};
    }
  `
}

export const Wrapper = styled.div<WrapperProps>`
  ${({ theme, error }) => error && wrapperModifiers.error(theme)}
  ${({ theme, disabled }) => disabled && wrapperModifiers.disabled(theme)}
`

export const Asterisk = styled.span<AsteriskProps>`
  font-size: ${({ theme }) => theme.ds.typography.size.small};
  font-weight: ${({ theme }) => theme.ds.typography.weight.normal};
  color: ${({ theme, requiredColor }) => theme.ds.colors[requiredColor!]};
  cursor: pointer;
`
